import { defaultHtmlAudioSessionFactory, defaultWebAudioSessionFactory } from './playerSessions.js';
import { AudioPlayerError } from './errors.js';

/**
 * Sesión interna de reproducción. Cada `play()` crea una nueva sesión;
 * llamar `play()` mientras hay audio sonando cancela la anterior y la
 * promesa del `play()` cortado **resuelve** (no rechaza) — la cancelación
 * es parte de la API esperada.
 */
export interface PlayerSession {
  /** Resuelve al terminar reproducción o ser cancelada. Rechaza en error. */
  readonly completed: Promise<void>;
  /** Detiene la reproducción y resuelve `completed`. Idempotente. */
  cancel(): void;
  /** Libera recursos. Idempotente. */
  dispose(): void;
}

export type HtmlAudioSessionFactory = (binary: ArrayBuffer, mimeType: string) => PlayerSession;
export type WebAudioSessionFactory = (binary: ArrayBuffer) => PlayerSession;

export interface AudioPlayerConfig {
  /**
   * Crea una sesión que reproduce con `HTMLAudioElement` (MP3 / WAV).
   * Default: `defaultHtmlAudioSessionFactory` (usa `Audio`, `Blob`,
   * `URL.createObjectURL`). Inyectable para tests sin DOM.
   */
  htmlAudioSessionFactory?: HtmlAudioSessionFactory;

  /**
   * Crea una sesión que reproduce con Web Audio API (OGG/Opus —
   * Safari no decodifica OGG/Opus en `HTMLAudioElement`,
   * `04_ARQUITECTURA.md` §4.1.2). Default: `defaultWebAudioSessionFactory`
   * (usa `AudioContext.decodeAudioData` + `AudioBufferSourceNode`).
   * Inyectable para tests.
   */
  webAudioSessionFactory?: WebAudioSessionFactory;
}

/**
 * `mime_type` del frame `audio.binario` que el reproductor sabe manejar
 * (JC §0.2). Para este cliente el servidor declara y manda `audio/mpeg`:
 *
 * - `audio/mpeg` (MP3) → HTMLAudioElement
 * - `audio/wav` → HTMLAudioElement
 * - `audio/ogg; codecs=opus` → Web Audio API
 */
const HTML_AUDIO_MIME_TYPES = ['audio/mpeg', 'audio/wav'];
const OGG_OPUS_PREFIX = 'audio/ogg';

/**
 * ¿Puede el reproductor manejar este `mime_type`? Es la capacidad que se
 * contrasta con el `downlink` declarado en el Paso 2
 * (`verificarAudioDeclarado`), antes de pedir el ticket.
 */
export function puedeReproducirMimeType(mimeType: string): boolean {
  return HTML_AUDIO_MIME_TYPES.includes(mimeType) || mimeType.startsWith(OGG_OPUS_PREFIX);
}

/**
 * Reproductor del audio del paciente. Stateless desde afuera (sin
 * `isPlaying`, sin eventos). El caller (F1 en `apps/web`) hace la
 * correlación JSON↔binario por `audio_ref_id` y orquesta el orden:
 * aplicar la expresión al avatar primero, luego `player.play()`. El
 * Player NO conoce turnos ni avatar.
 *
 * Patrón de uso:
 *
 * ```ts
 * const player = new AudioPlayer();
 * await player.play(binary, 'audio/mpeg'); // resuelve al terminar
 * // un nuevo play() corta el anterior; la promesa anterior resuelve.
 * ```
 */
export class AudioPlayer {
  private readonly htmlAudioSessionFactory: HtmlAudioSessionFactory;
  private readonly webAudioSessionFactory: WebAudioSessionFactory;
  private currentSession: PlayerSession | null = null;

  constructor(config: AudioPlayerConfig = {}) {
    this.htmlAudioSessionFactory = config.htmlAudioSessionFactory ?? defaultHtmlAudioSessionFactory;
    this.webAudioSessionFactory = config.webAudioSessionFactory ?? defaultWebAudioSessionFactory;
  }

  /**
   * Reproduce el binario decodificándolo según `mimeType`. Resuelve cuando
   * termina la reproducción. Si llega otro `play()` antes, esta promesa
   * resuelve (sin rechazar) y arranca el nuevo audio.
   *
   * @throws {AudioPlayerError} con `code: 'unsupported_format'` si el
   *   mimeType no es uno de los que maneja (ver `puedeReproducirMimeType`).
   * @throws {AudioPlayerError} con `code: 'decode_failed'` si Web Audio
   *   no decodifica el OGG/Opus.
   * @throws {AudioPlayerError} con `code: 'playback_failed'` si la
   *   reproducción falla (autoplay bloqueado, codec no instalado).
   */
  async play(binary: ArrayBuffer, mimeType: string): Promise<void> {
    this.currentSession?.cancel();

    const session = this.createSession(binary, mimeType);
    this.currentSession = session;

    try {
      await session.completed;
    } finally {
      if (this.currentSession === session) {
        this.currentSession = null;
      }
      session.dispose();
    }
  }

  /** Detiene la reproducción actual si existe. La promesa del `play()` resuelve. */
  stop(): void {
    this.currentSession?.cancel();
  }

  /** Libera la sesión actual. Idempotente. */
  dispose(): void {
    this.stop();
  }

  private createSession(binary: ArrayBuffer, mimeType: string): PlayerSession {
    if (HTML_AUDIO_MIME_TYPES.includes(mimeType)) {
      return this.htmlAudioSessionFactory(binary, mimeType);
    }
    if (mimeType.startsWith(OGG_OPUS_PREFIX)) {
      return this.webAudioSessionFactory(binary);
    }
    throw new AudioPlayerError('unsupported_format', `mimeType "${mimeType}" is not playable`);
  }
}
